import { DATE_FORMATS } from '../constants';

/**
 * Format date to API format (DD-MM-YYYY)
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
export const formatDateForApi = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Format date for display (DD/MM/YYYY)
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Parse API date string (DD-MM-YYYY) to Date object
 * @param {string} dateString - Date string from API
 * @returns {Date} Date object
 */
export const parseApiDate = (dateString) => {
  if (!dateString) return null;
  const [day, month, year] = dateString.split('-');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

/**
 * Get first day of month
 * @param {Date} date - Reference date
 * @returns {Date} First day of the month
 */
export const getFirstDayOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Get last day of month
 * @param {Date} date - Reference date
 * @returns {Date} Last day of the month
 */
export const getLastDayOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * Get date range for current month
 * @param {Date} date - Reference date (default: current date)
 * @returns {object} Object with tungay and denngay in API format
 */
export const getCurrentMonthRange = (date = new Date()) => {
  const firstDay = getFirstDayOfMonth(date);
  const lastDay = getLastDayOfMonth(date);
  
  return {
    tungay: formatDateForApi(firstDay),
    denngay: formatDateForApi(lastDay)
  };
};

/**
 * Get week days starting from a given date
 * @param {Date} startDate - Start date
 * @returns {Array} Array of Date objects for the week
 */
export const getWeekDays = (startDate) => {
  const week = [];
  const start = new Date(startDate);
  const day = start.getDay();
  const diff = start.getDate() - day;
  start.setDate(diff);

  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    week.push(day);
  }
  return week;
};

/**
 * Get days in month for calendar display
 * @param {Date} date - Reference date
 * @returns {Array} Array of day numbers (null for empty cells)
 */
export const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return days;
};

/**
 * Check if two dates are the same day
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} True if same day
 */
export const isSameDay = (date1, date2) => {
  return date1.getDate() === date2.getDate() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getFullYear() === date2.getFullYear();
};

/**
 * Get day name in Vietnamese
 * @param {Date} date - Date object
 * @returns {string} Day name
 */
export const getDayName = (date) => {
  const days = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
  return days[date.getDay()];
};

/**
 * Get month name in Vietnamese
 * @param {Date} date - Date object
 * @returns {string} Month name
 */
export const getMonthName = (date) => {
  const months = [
    'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4',
    'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8',
    'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'
  ];
  return months[date.getMonth()];
};
